import { Box, Button, SimpleGrid } from '@chakra-ui/react';
import type { SystemStyleObject } from '@chakra-ui/react';
import {
  Calendar,
  Camera,
  ExternalLink,
  Facebook,
  Gift,
  Globe,
  Heart,
  Instagram,
  LucideIcon,
  Mail,
  MapPin,
  Menu,
  Music,
  Ribbon,
  ShoppingBag,
  Star,
  Ticket,
  Trophy,
  Twitter,
  Utensils,
  Youtube,
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Restaurant } from '@/types/restaurant';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

/**
 * Opens a URL in the Capacitor in-app browser on native platforms,
 * falling back to a regular new tab in browser dev mode.
 *
 * @param url - The URL to open (must be http/https)
 */
export async function openInAppBrowser(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      await Browser.open({ url, presentationStyle: 'popover' });
      return;
    } catch (err) {
      // In-app browser unavailable — fall through to window.open
      console.warn('In-app browser failed, opening externally:', err);
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Mapping of icon names to Lucide icon components.
 * Used to dynamically render icons for custom links based on the icon name
 * configured in the backend.
 *
 * IMPORTANT: This list must match ALLOWED_ICONS in
 * paire-api/paire_api/serializers/business_portal.py
 * If adding icons, update both files.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  'external-link': ExternalLink,
  menu: Menu,
  calendar: Calendar,
  gift: Gift,
  star: Star,
  heart: Heart,
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  youtube: Youtube,
  mail: Mail,
  globe: Globe,
  'map-pin': MapPin,
  utensils: Utensils,
  'shopping-bag': ShoppingBag,
  ticket: Ticket,
  music: Music,
  camera: Camera,
  trophy: Trophy,
  ribbon: Ribbon,
};

// =============================================================================
// Gradient Border Helper
// =============================================================================

/**
 * Shared keyboard-focus ring (2px solid blue.300, 2px offset) — this is the
 * project's standing `_focusVisible` convention. Exported so other
 * restaurant-profile action components (e.g. `ProfileActionBar.tsx`,
 * `RestaurantHero.tsx`'s Photos pill) reuse the identical ring instead of
 * re-deriving it, rather than each defining its own slightly-different outline.
 */
export const FOCUS_VISIBLE_RING: SystemStyleObject = {
  outline: '2px solid',
  outlineColor: 'blue.300',
  outlineOffset: '2px',
};

/**
 * Creates a gradient border effect using CSS mask-composite, producing a
 * transparent-center gradient stroke identical to the UnifiedChip technique.
 *
 * The returned sx object adds a ::before pseudo-element with the gradient
 * as its background, masked so only the border region is visible.
 *
 * @param gradient - CSS linear-gradient string for the border
 * @param borderWidth - Border thickness in pixels (default 2)
 */
function gradientBorderSx(gradient: string, borderWidth = 2): SystemStyleObject {
  return {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: `${borderWidth}px`,
      background: gradient,
      overflow: 'hidden',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
    '&:disabled::before': {
      opacity: 0.4,
    },
    _focusVisible: FOCUS_VISIBLE_RING,
  };
}

// =============================================================================
// Button Gradient Definitions
// =============================================================================

/**
 * Gradient definitions for the contact buttons that live in the Get There
 * card (`ContactActions`), using the 300-500 color range for a softer
 * appearance that complements the gradient-border aesthetic. Order and
 * custom-link chips no longer use gradients (paire-app#129 Option C).
 */
const BUTTON_GRADIENTS: Record<string, string> = {
  directions: 'linear-gradient(135deg, #B794F6, #7C3AED)',  // purple.300 -> purple.600
  call: 'linear-gradient(135deg, #6EE7B7, #379d4b)',        // emerald.300 -> custom green.600
  website: 'linear-gradient(135deg, #93C5FD, #3B82F6)',     // blue.300 -> blue.500
};

// =============================================================================
// Button Style Tiers
// =============================================================================

/**
 * Hardcoded shadow colors per primary button type to avoid brittle regex
 * extraction from gradient strings.
 */
const PRIMARY_SHADOW_COLORS: Record<string, string> = {
  directions: 'rgba(124, 58, 237, 0.25)', // #7C3AED (purple.600) at 25% opacity
};

type ButtonTier = 'primary' | 'secondary';

interface ButtonStyleConfig {
  bg: string;
  textColor: string;
  shadow?: string;
  hoverBg: string;
  sx?: SystemStyleObject;
}

/**
 * Returns a style configuration object for a contact button type and tier.
 *
 * These buttons render inside a `cardSurfaceProps` card (the Get There
 * card), so text/icon/fill/hover read from the `--card-*`/`--card-chip-*`
 * token system (D66 Phase 2). The gradient border carries the WCAG 1.4.11
 * boundary against the card — the fill does not.
 *
 * Tiers:
 *   - primary   (Directions): gradient border + --card-chip-bg fill,
 *               --card-text text, soft glow shadow
 *   - secondary (Call, Website): gradient border + --card-chip-bg fill,
 *               --card-text text, no shadow
 */
function getButtonStyles(buttonType: string, tier: ButtonTier): ButtonStyleConfig {
  const gradient = BUTTON_GRADIENTS[buttonType];
  if (tier === 'primary') {
    // Drop-shadow alpha-black, not a text/icon/border contrast-bearing
    // colour in the WCAG sense D66 targets — deliberately out of scope for
    // the literal guard and this fix (see D66 Phase 2 result doc).
    const shadowColor = PRIMARY_SHADOW_COLORS[buttonType] ?? 'rgba(0,0,0,0.15)';
    return {
      bg: 'var(--card-chip-bg)',
      textColor: 'var(--card-text)',
      shadow: `0 2px 10px ${shadowColor}`,
      hoverBg: 'var(--card-chip-hover)',
      sx: gradientBorderSx(gradient),
    };
  }
  return {
    bg: 'var(--card-chip-bg)',
    textColor: 'var(--card-text)',
    hoverBg: 'var(--card-chip-hover)',
    sx: gradientBorderSx(gradient),
  };
}

/** Pre-computed styles — safe at module level since `getButtonStyles` is pure. */
export const DIRECTIONS_STYLES = getButtonStyles('directions', 'primary');
export const CALL_STYLES = getButtonStyles('call', 'secondary');
export const WEBSITE_STYLES = getButtonStyles('website', 'secondary');

/**
 * Neutral link-chip treatment shared by Order and custom links: transparent
 * fill, solid --card-chip-border outline, --card-text label. Only the small
 * leading icon carries color (paire-app#129 Option C).
 */
const CHIP_STYLES = {
  bg: 'transparent',
  textColor: 'var(--card-text)',
  hoverBg: 'var(--card-chip-hover)',
  border: '2px solid',
  borderColor: 'var(--card-chip-border)',
  fontWeight: 500,
} as const;

/** Order's leading-icon color (brand teal token). */
const ORDER_ICON_COLOR = 'var(--color-icon-teal)';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Chakra color scheme names allowed for custom link buttons.
 * Must stay in sync with ALLOWED_COLOR_PRESETS in
 * paire-api/paire_api/serializers/business_portal.py
 */
const CHAKRA_COLOR_SCHEMES = new Set([
  'gray', 'red', 'orange', 'yellow', 'green',
  'teal', 'blue', 'cyan', 'purple', 'pink',
]);

/**
 * Resolves a business-configured custom link color to a CSS color for the
 * chip's ICON only. Supports Chakra color scheme names (e.g. "purple" →
 * "purple.400") and hex strings (e.g. "#FF6B35" → "#FF6B35"). Falls back to
 * `--card-text-2`.
 */
function resolveCustomLinkIconColor(color?: string): string {
  if (!color) return 'var(--card-text-2)';
  if (CHAKRA_COLOR_SCHEMES.has(color)) return `${color}.400`;
  if (color.startsWith('#')) return color;
  return 'var(--card-text-2)';
}

/**
 * Returns the Lucide icon component for a given icon name.
 * Defaults to ExternalLink if the icon name is not found or not provided.
 */
const getIconComponent = (iconName?: string): LucideIcon => {
  if (!iconName) {
    return ExternalLink;
  }
  return ICON_MAP[iconName] || ExternalLink;
};

/**
 * Validates that a URL uses a safe protocol (http or https).
 * Prevents javascript: and other dangerous URL schemes.
 */
const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

/** Custom links that are renderable (max 4, valid http/https URL). */
const getValidCustomLinks = (restaurant: Restaurant) =>
  (restaurant?.customLinks?.slice(0, 4) || []).filter(
    (link) => link.url && isValidUrl(link.url)
  );

/**
 * True when `ActionButtons` would render at least one chip (Order or a valid
 * custom link). Callers use it to avoid wrapping an empty component in a card.
 */
export function hasLinkChips(restaurant: Restaurant): boolean {
  return Boolean(restaurant?.orderswebsite) || getValidCustomLinks(restaurant).length > 0;
}

// =============================================================================
// Component
// =============================================================================

interface ActionButtonsProps {
  restaurant: Restaurant;
}

/**
 * Viewport widths for the chip grid: ONE column below 340px (two cells would
 * leave ~130px chips at 320px), TWO columns from 340px up to `lg`, ONE per row
 * from `lg` (62em — the breakpoint at which the page's sidebar appears).
 */
const CHIP_TWO_COLUMN_QUERY = '@media (min-width: 340px) and (max-width: 61.99em)';
const CHIP_GRID_SX: SystemStyleObject = {
  [CHIP_TWO_COLUMN_QUERY]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
};

/** Shared layout props for every neutral chip. */
const chipProps = {
  // Labels wrap to two lines (full text also in aria-label/title); the chip
  // grows with them, never below the 48px target.
  h: 'auto',
  minH: '48px',
  px: 3,
  py: 2,
  // Each chip fills its grid cell; `minW: 0` lets the label ellipsize
  // instead of stretching the (1fr) column.
  w: '100%',
  minW: 0,
  justifyContent: 'flex-start',
  textAlign: 'left',
  whiteSpace: 'normal',
  variant: 'unstyled',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  borderRadius: 'var(--r-card-compact)',
  bg: CHIP_STYLES.bg,
  color: CHIP_STYLES.textColor,
  border: CHIP_STYLES.border,
  borderColor: CHIP_STYLES.borderColor,
  fontWeight: CHIP_STYLES.fontWeight,
  fontSize: 'md',
  _hover: { bg: CHIP_STYLES.hoverBg },
  _focusVisible: FOCUS_VISIBLE_RING,
} as const;

/**
 * Order + business-set custom-link chips (paire-app#129). From 340px up to
 * `lg` (the mobile card) they form a 2-column grid; when the chip count is odd
 * the LAST chip spans both columns so no cell is left empty (Order + 4 custom
 * links = 5 chips, so the last custom link spans). Below 340px, and from `lg`
 * up (the desktop sidebar, the same breakpoint the page layout uses), it is
 * ONE chip per row at full width, so the span rule does not apply. Labels wrap
 * to two lines before truncating.
 * Directions, Call and Website moved to `ContactActions` inside the Get There
 * card; Photos moved to a pill in the hero. Renders nothing when there is
 * neither an Order link nor a valid custom link.
 */
export function ActionButtons({ restaurant }: ActionButtonsProps) {
  const customLinks = getValidCustomLinks(restaurant);
  if (!hasLinkChips(restaurant)) return null;

  const hasOrder = Boolean(restaurant?.orderswebsite);
  const chipCount = customLinks.length + (hasOrder ? 1 : 0);
  /** In the two-column range (340px to `lg`), the last chip spans both columns when the count is odd. */
  const spanProps = (position: number) =>
    chipCount % 2 === 1 && position === chipCount - 1 ? { sx: { [CHIP_TWO_COLUMN_QUERY]: { gridColumn: 'span 2' } } } : {};

  return (
    <SimpleGrid columns={1} gap={2} width="100%" sx={CHIP_GRID_SX}>
      {hasOrder && (
        <Button
          {...chipProps}
          {...spanProps(0)}
          title={`Order from ${restaurant?.name}`}
          aria-label={`Order from ${restaurant?.name}`}
          onClick={() => {
            analytics.capture(AnalyticsEvents.RESTAURANT_ORDERS_CLICKED, {
              restaurant_id: restaurant?.id,
              restaurant_name: restaurant?.name,
              link_url: restaurant.orderswebsite,
            });
            if (restaurant.orderswebsite) {
              openInAppBrowser(restaurant.orderswebsite);
            }
          }}
        >
          <Box as="span" color={ORDER_ICON_COLOR} display="inline-flex" flexShrink={0} aria-hidden>
            <ShoppingBag size={20} />
          </Box>
          <Box as="span" noOfLines={2} minW={0}>Order</Box>
        </Button>
      )}
      {customLinks.map((link, index) => {
        const IconComponent = getIconComponent(link.icon);
        return (
          <Button
            key={`custom-link-${index}-${link.url}`}
            {...chipProps}
            {...spanProps(index + (hasOrder ? 1 : 0))}
            title={link.label}
            aria-label={`${link.label} - opens in app`}
            onClick={() => {
              analytics.capture(AnalyticsEvents.RESTAURANT_CUSTOM_LINK_CLICKED, {
                restaurant_id: restaurant?.id,
                restaurant_name: restaurant?.name,
                link_url: link.url,
                link_label: link.label,
              });
              openInAppBrowser(link.url);
            }}
          >
            <Box
              as="span"
              color={resolveCustomLinkIconColor(link.color)}
              display="inline-flex"
              flexShrink={0}
              aria-hidden
            >
              <IconComponent size={20} />
            </Box>
            <Box as="span" noOfLines={2} minW={0} wordBreak="break-word">{link.label}</Box>
          </Button>
        );
      })}
    </SimpleGrid>
  );
}
